import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { logoutClient } from './api';
import { useAuth } from './useAuth';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        headerBg: isDark ? '#2a2a2a' : '#f8f9fa',
        border: isDark ? '#444' : '#dee2e6',
        link: isDark ? '#6cb2f7' : '#007bff',
        buttonText: isDark ? '#121212' : '#fff',
        menuItemBg: isDark ? '#333' : '#fff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
        containerBg: isDark ? '#121212' : '#f4f6f8',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        success: isDark ? '#4caf50' : '#28a745',
        warning: isDark ? '#ff9800' : '#ffc107',
        danger: isDark ? '#f44336' : '#dc3545',
        info: isDark ? '#2196f3' : '#17a2b8',
        primary: isDark ? '#6cb2f7' : '#007bff',
        secondary: isDark ? '#9e9e9e' : '#6c757d',
        inputBg: isDark ? '#333' : '#fff',
        inputBorder: isDark ? '#555' : '#ddd',
        inputText: isDark ? '#e0e0e0' : '#212529',
    };

    return {
        pageWrapper: { fontFamily: 'system-ui, -apple-system, sans-serif', minHeight: '100vh', backgroundColor: colors.containerBg },
        statusBar: {
            position: 'sticky', top: 0, zIndex: 100,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 24px',
            padding: '14px 24px', backgroundColor: colors.cardBg, borderBottom: `1px solid ${colors.border}`,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.06)'
        },
        statusBarItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' },
        statusBarLabel: { color: colors.secondary, fontWeight: '500' },
        statusBarValue: { color: colors.text, fontWeight: '600' },
        statusBarChip: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
        container: { maxWidth: '900px', margin: '0 auto', padding: '24px', color: colors.text },
        header: { fontSize: '22px', fontWeight: '600', marginBottom: '20px', color: colors.text },
        navLink: { display: 'inline-block', marginBottom: '16px', textDecoration: 'none', color: colors.link, fontSize: '14px', fontWeight: '500' },
        filterBar: { marginBottom: '20px', padding: '14px 18px', backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '10px', boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04)' },
        sectionHeader: {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px',
            padding: '14px 18px', marginTop: '28px', marginBottom: '12px',
            backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '10px',
            boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04)'
        },
        sectionHeaderMain: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
        sectionTitle: { fontSize: '17px', fontWeight: '600', color: colors.primary, margin: 0 },
        sectionSummary: { fontSize: '13px', color: colors.secondary, fontWeight: '500' },
        sectionActions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
        cardToggleButton: {
            width: '30px', height: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '8px', border: `1px solid ${colors.border}`, backgroundColor: colors.inputBg,
            color: colors.text, cursor: 'pointer', fontSize: '16px', fontWeight: '700', lineHeight: 1
        },
        markAllButton: { padding: '6px 14px', fontSize: '13px', cursor: 'pointer', backgroundColor: colors.info, color: colors.buttonText, border: 'none', borderRadius: '6px', fontWeight: '500' },
        collapseControls: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: '12px' },
        collapseControlButton: { padding: '6px 12px', fontSize: '13px', cursor: 'pointer', backgroundColor: colors.inputBg, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: '6px', fontWeight: '500' },
        input: { padding: '6px 10px', width: '72px', backgroundColor: colors.inputBg, border: `1px solid ${colors.inputBorder}`, color: colors.inputText, borderRadius: '6px', fontSize: '14px' },
        /* Status row - compact single-row layout */
        item: {
            display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: '16px', alignItems: 'center',
            padding: '12px 18px', marginBottom: '8px', borderRadius: '8px',
            backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`,
            boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.03)',
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease'
        },
        itemStatusBadge: { width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 },
        itemMain: { minWidth: 0 },
        itemMeta: { display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'center', fontSize: '13px', color: colors.text },
        itemMetaPart: { display: 'flex', alignItems: 'center', gap: '4px' },
        itemMetaLabel: { color: colors.secondary, fontWeight: '500' },
        itemTimestamp: { fontSize: '12px', color: colors.secondary, whiteSpace: 'nowrap' },
        statusCheckbox: { height: '22px', width: '22px', cursor: 'pointer', flexShrink: 0 },
        notes: { gridColumn: '1 / -1', width: '100%', marginTop: '8px', padding: '10px 12px', boxSizing: 'border-box', backgroundColor: colors.inputBg, border: `1px solid ${colors.inputBorder}`, color: colors.inputText, borderRadius: '6px', fontSize: '14px', resize: 'vertical', minHeight: '60px' },
        notesToggle: { fontSize: '12px', color: colors.link, cursor: 'pointer', marginTop: '4px', fontWeight: '500' },
        button: { padding: '10px 18px', backgroundColor: colors.success, color: colors.buttonText, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
        completionFooter: { marginTop: '24px', padding: '18px 20px', backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '10px', boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04)' },
        success: colors.success,
        primary: colors.primary,
        warning: colors.warning,
        info: colors.info,
        buttonText: colors.buttonText,
        inputBg: colors.inputBg,
        inputBorder: colors.inputBorder,
        inputText: colors.inputText,
        text: colors.text,
        secondary: colors.secondary,
        border: colors.border
    };
};

const ChecklistItemRow = ({ item, styles, isViewer, onFieldUpdate }) => {
    const [notesExpanded, setNotesExpanded] = useState(!!item.notes);
    const hasNotes = !!item.notes?.trim();
    return (
        <div style={styles.item}>
            <div style={{
                ...styles.itemStatusBadge,
                backgroundColor: item.status === 'Completed' ? 'rgba(40, 167, 69, 0.15)' : 'rgba(108, 117, 125, 0.15)',
                color: item.status === 'Completed' ? styles.success : styles.secondary
            }}>
                {item.status === 'Completed' ? '✓' : '○'}
            </div>
            <div style={styles.itemMain}>
                <div style={styles.itemMeta}>
                    <span style={styles.itemMetaPart}>
                        <span style={styles.itemMetaLabel}>Area:</span>
                        <span>{item.work_area || 'N/A'}</span>
                    </span>
                    <span style={styles.itemMetaPart}>
                        <span style={styles.itemMetaLabel}>WC:</span>
                        <span>{item.work_center || 'N/A'}</span>
                    </span>
                    {item.steps && (
                        <span style={styles.itemMetaPart}>
                            <span style={styles.itemMetaLabel}>Step:</span>
                            <span>{item.steps}</span>
                        </span>
                    )}
                </div>
            </div>
            <div style={styles.itemTimestamp}>
                {item.status === 'Completed' && item.completed_at
                    ? new Date(item.completed_at).toLocaleString()
                    : '—'}
            </div>
            <input
                type="checkbox"
                checked={item.status === 'Completed'}
                disabled={isViewer}
                onChange={(e) => onFieldUpdate(item.id, 'status', e.target.checked ? 'Completed' : 'Not Started')}
                style={styles.statusCheckbox}
                title={item.status === 'Completed' ? 'Completed' : 'Mark complete'}
            />
            <div style={{ gridColumn: '1 / -1', marginTop: notesExpanded ? '8px' : '4px' }}>
                {notesExpanded && (
                    <textarea
                        placeholder="Add notes..."
                        defaultValue={item.notes}
                        disabled={isViewer}
                        onBlur={(e) => onFieldUpdate(item.id, 'notes', e.target.value)}
                        style={styles.notes}
                    />
                )}
                <span
                    style={styles.notesToggle}
                    onClick={() => setNotesExpanded(!notesExpanded)}
                >
                    {notesExpanded ? '▲ Hide notes' : (hasNotes ? `▼ Notes: ${String(item.notes || '').slice(0, 50)}${(item.notes?.length || 0) > 50 ? '…' : ''}` : '▼ Add notes')}
                </span>
            </div>
        </div>
    );
};

const sortCards = (a, b) => {
    const parseCard = (cardStr) => {
        if (!cardStr) return [0, 0];
        const parts = String(cardStr).split('-');
        const major = parseInt(parts[0], 10) || 0;
        const minor = parseInt(parts[1], 10) || 0;
        return [major, minor];
    };
    
    const extractStepNumber = (stepStr) => {
        if (!stepStr) return 0;
        // Try to extract the last number from the step string
        const matches = String(stepStr).match(/(\d+)(?!.*\d)/);
        return matches ? parseInt(matches[1], 10) : 0;
    };
    
    const [aMajor, aMinor] = parseCard(a.card);
    const [bMajor, bMinor] = parseCard(b.card);
    if (aMajor !== bMajor) return aMajor - bMajor;
    if (aMinor !== bMinor) return aMinor - bMinor;
    // Sort by steps if cards are the same - extract number from step text
    const aSteps = extractStepNumber(a.steps);
    const bSteps = extractStepNumber(b.steps);
    return aSteps - bSteps;
};

function ChecklistPage({ theme = 'light' }) {
    const [checklistItems, setChecklistItems] = useState([]);
    const [inspectionInfo, setInspectionInfo] = useState(null);
    const [manHours, setManHours] = useState({});
    const [workCenterFilter, setWorkCenterFilter] = useState('');
    const [expandedCards, setExpandedCards] = useState({});
    const { inspectionId } = useParams();
    const navigate = useNavigate();
    const { groups } = useAuth();
    const isViewer = groups.includes('Viewer') && !groups.includes('Inspector');
    
    const styles = getStyles(theme);

    const handleLogout = useCallback(() => {
        logoutClient();
        navigate('/login');
    }, [navigate]);

    useEffect(() => {
        if (inspectionId) {
            Promise.all([
                api.get(`/inspections/${inspectionId}/`),
                api.get(`/inspections/${inspectionId}/checklist/`),
                api.get(`/man-hours/?inspection=${inspectionId}`)
            ]).then(([inspectionRes, checklistRes, manHoursRes]) => {
                // Ensure we have an array for checklist data
                const checklistData = Array.isArray(checklistRes.data) ? checklistRes.data : [];
                const sortedChecklist = Array.isArray(checklistData) ? [...checklistData].sort(sortCards) : [];
                setInspectionInfo(inspectionRes.data);
                setChecklistItems(sortedChecklist);
                
                // Handle paginated response for man-hours
                let manHoursData = manHoursRes.data;
                if (manHoursRes.data.results) {
                    manHoursData = manHoursRes.data.results;
                }
                
                const hoursMap = Array.isArray(manHoursData) ? manHoursData.reduce((acc, mh) => {
                    acc[mh.card] = { id: mh.id, hours: mh.hours };
                    return acc;
                }, {}) : {};
                setManHours(hoursMap);
            }).catch(err => {
                console.error("Error fetching data:", err);
                if (err.response && err.response.status === 401) handleLogout();
                // Set empty arrays on error to prevent map errors
                setChecklistItems([]);
                setManHours({});
            });
        }
    }, [inspectionId, handleLogout]);
    
    const handleFieldUpdate = (itemId, field, value) => {
        if (inspectionInfo.status === 'Not Started' && field === 'status' && value === 'Completed') {
            api.patch(`/inspections/${inspectionId}/`, { status: 'In Progress' })
                .then(res => setInspectionInfo(res.data))
                .catch(err => console.error("Error updating inspection status:", err));
        }
        api.patch(`/checklist-items/${itemId}/`, { [field]: value })
            .then(res => {
                setChecklistItems(prevItems => Array.isArray(prevItems) ? prevItems.map(item => item.id === itemId ? res.data : item) : []);
                
                // Refresh inspection info to get updated start_date
                if (field === 'status' && value === 'Completed' && inspectionInfo.status === 'Not Started') {
                    api.get(`/inspections/${inspectionId}/`)
                        .then(res => setInspectionInfo(res.data))
                        .catch(err => console.error("Error refreshing inspection info:", err));
                }
            })
            .catch(err => console.error(`Error updating ${field} for item ${itemId}:`, err));
    };

    const handleManHoursUpdate = (card, hours) => {
        const existingEntry = manHours[card];
        const data = { inspection: inspectionId, card: card, hours: hours || 0 };
        if (existingEntry) {
            api.patch(`/man-hours/${existingEntry.id}/`, data);
        } else {
            api.post('/man-hours/', data).then(res => {
                setManHours(prev => ({...prev, [card]: { id: res.data.id, hours: res.data.hours }}));
            });
        }
    };

    const handleMarkGroupPassed = (cardValue) => {
        const itemIdsToUpdate = Array.isArray(checklistItems) ? checklistItems
            .filter(item => item.card === cardValue)
            .map(item => item.id) : [];
        
        const apiPromises = Array.isArray(itemIdsToUpdate) ? itemIdsToUpdate.map(itemId => 
            api.patch(`/checklist-items/${itemId}/`, { status: 'Completed' })
        ) : [];
        Promise.all(apiPromises).then(() => {
            api.get(`/inspections/${inspectionId}/checklist/`).then(res => {
                // Ensure we have an array for checklist data
                const checklistData = Array.isArray(res.data) ? res.data : [];
                const sortedChecklist = Array.isArray(checklistData) ? [...checklistData].sort(sortCards) : [];
                setChecklistItems(sortedChecklist);
            });
        }).catch(err => console.error("Error during bulk update:", err));
    };
    
    const handleCompleteInspection = () => {
        // Check if all checklist items are completed
        const incompleteItems = Array.isArray(checklistItems) ? checklistItems.filter(item => item.status !== 'Completed') : [];
        
        if (incompleteItems.length > 0) {
            const incompleteCards = [...new Set(incompleteItems.map(item => item.card))];
            const cardList = incompleteCards.sort((a, b) => sortCards({ card: a }, { card: b })).join(', ');
            
            alert(`Cannot complete inspection! You have ${incompleteItems.length} unchecked items in the following cards: ${cardList}\n\nPlease complete all checklist items before marking the inspection as complete.`);
            return;
        }
        
        api.patch(`/inspections/${inspectionId}/`, { status: 'Completed' })
            .then(() => {
                alert('Inspection has been marked as complete!');
                navigate('/');
            })
            .catch(err => console.error("Error marking inspection as complete:", err));
    };

    const handleJumpToCard = (card) => {
        setExpandedCards(prev => ({ ...prev, [card]: true }));
        setTimeout(() => {
            const element = document.getElementById(`card-${card}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 0);
    };

    const handleToggleCard = (card) => {
        setExpandedCards(prev => ({ ...prev, [card]: !prev[card] }));
    };

    const handleExpandAllCards = () => {
        setExpandedCards(sortedCardKeys.reduce((acc, card) => ({ ...acc, [card]: true }), {}));
    };

    const handleCollapseAllCards = () => {
        setExpandedCards({});
    };

    // Filter items by work center if filter is set
    const filteredItems = Array.isArray(checklistItems) ? checklistItems.filter(item => {
        if (!workCenterFilter) return true;
        return item.work_center && item.work_center.toLowerCase().includes(workCenterFilter.toLowerCase());
    }) : [];

    const groupedChecklist = Array.isArray(filteredItems) ? filteredItems.reduce((acc, item) => {
        const key = item.card || 'General';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {}) : {};
    
    const sortedCardKeys = Array.isArray(Object.keys(groupedChecklist)) ? Object.keys(groupedChecklist).sort((a, b) => sortCards({ card: a }, { card: b })) : [];
    const fullGroupedChecklist = Array.isArray(checklistItems) ? checklistItems.reduce((acc, item) => {
        const key = item.card || 'General';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {}) : {};
    const fullSortedCardKeys = Object.keys(fullGroupedChecklist).sort((a, b) => sortCards({ card: a }, { card: b }));

    const totalItems = checklistItems.length;
    const completedItems = checklistItems.filter(item => item.status === 'Completed').length;
    const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
    const resumeCard = fullSortedCardKeys.find(card => (fullGroupedChecklist[card] || []).some(item => item.status !== 'Completed'));
    const cardsRemaining = fullSortedCardKeys
        .map(card => ({ card, incomplete: (fullGroupedChecklist[card] || []).filter(item => item.status !== 'Completed').length }))
        .filter(({ incomplete }) => incomplete > 0);
    const isComplete = totalItems > 0 && completedItems === totalItems;

    if (!inspectionInfo) return <div>Loading...</div>;

    return (
        <div style={styles.pageWrapper}>
            {/* Sticky status bar - all key info at a glance */}
            <div style={styles.statusBar}>
                <div style={styles.statusBarItem}>
                    <span style={styles.statusBarLabel}>BUNO</span>
                    <span style={styles.statusBarValue}>{inspectionInfo.buno}</span>
                </div>
                <div style={styles.statusBarItem}>
                    <span style={styles.statusBarLabel}>Type</span>
                    <span style={styles.statusBarValue}>{inspectionInfo.checklist_type || '—'}</span>
                </div>
                <div style={styles.statusBarItem}>
                    <span style={styles.statusBarLabel}>Progress</span>
                    <span style={styles.statusBarValue}>{completedItems}/{totalItems}</span>
                    <span style={{ ...styles.statusBarValue, color: completionPercentage === 100 ? styles.success : styles.primary }}>({completionPercentage}%)</span>
                </div>
                <div style={styles.statusBarItem}>
                    <span style={styles.statusBarLabel}>Resume at</span>
                    <span style={styles.statusBarValue}>{resumeCard ? `Card ${resumeCard}` : isComplete ? 'All done' : '—'}</span>
                    {resumeCard && (
                        <span
                            style={{ ...styles.statusBarChip, backgroundColor: styles.primary, color: styles.buttonText }}
                            onClick={() => handleJumpToCard(resumeCard)}
                        >
                            Jump
                        </span>
                    )}
                </div>
                {cardsRemaining.length > 0 && (
                    <div style={styles.statusBarItem}>
                        <span style={styles.statusBarLabel}>Remaining</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {cardsRemaining.map(({ card, incomplete }) => (
                                <span
                                    key={card}
                                    style={{ ...styles.statusBarChip, backgroundColor: styles.inputBg, color: styles.text, border: `1px solid ${styles.border}` }}
                                    onClick={() => handleJumpToCard(card)}
                                    title={`${incomplete} item${incomplete !== 1 ? 's' : ''} left`}
                                >
                                    {card} ({incomplete})
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div style={styles.container}>
                <Link to="/inspections" style={styles.navLink}>&larr; Back to Inspections</Link>
                <h2 style={styles.header}>Checklist for BUNO: {inspectionInfo.buno} ({inspectionInfo.checklist_type || 'Unknown Type'})</h2>
                
                {/* Work Center Filter */}
                <div style={styles.filterBar}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: styles.text }}>
                        Filter by Work Center
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            placeholder="Enter work center..."
                            value={workCenterFilter}
                            onChange={(e) => setWorkCenterFilter(e.target.value)}
                            style={{
                                width: '260px',
                                padding: '8px 12px',
                                border: `1px solid ${styles.inputBorder}`,
                                borderRadius: '6px',
                                backgroundColor: styles.inputBg,
                                color: styles.inputText,
                                fontSize: '14px'
                            }}
                        />
                        {workCenterFilter && (
                            <button
                                onClick={() => setWorkCenterFilter('')}
                                style={{
                                    padding: '8px 14px',
                                    backgroundColor: styles.info,
                                    color: styles.buttonText,
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500'
                                }}
                            >
                                Clear
                            </button>
                        )}
                        {workCenterFilter && (
                            <span style={{ fontSize: '13px', color: styles.secondary }}>
                                {filteredItems.length} of {checklistItems.length} items
                            </span>
                        )}
                    </div>
                </div>

                {Array.isArray(sortedCardKeys) && sortedCardKeys.length > 0 && (
                    <div style={styles.collapseControls}>
                        <button type="button" onClick={handleExpandAllCards} style={styles.collapseControlButton}>
                            Expand All Cards
                        </button>
                        <button type="button" onClick={handleCollapseAllCards} style={styles.collapseControlButton}>
                            Collapse All Cards
                        </button>
                    </div>
                )}
                
                {Array.isArray(sortedCardKeys) && sortedCardKeys.map(card => {
                    const cardItems = groupedChecklist[card] || [];
                    const cardCompletedItems = cardItems.filter(item => item.status === 'Completed').length;
                    const cardTotalItems = cardItems.length;
                    const cardRemainingItems = cardTotalItems - cardCompletedItems;
                    const isCardExpanded = !!expandedCards[card];

                    return (
                    <div key={card}>
                        <div id={`card-${card}`} style={styles.sectionHeader}>
                            <div style={styles.sectionHeaderMain}>
                                <button
                                    type="button"
                                    onClick={() => handleToggleCard(card)}
                                    style={styles.cardToggleButton}
                                    aria-expanded={isCardExpanded}
                                    aria-controls={`card-items-${card}`}
                                    title={isCardExpanded ? 'Collapse card details' : 'Expand card details'}
                                >
                                    {isCardExpanded ? '^' : 'v'}
                                </button>
                                <div>
                                    <h3 style={styles.sectionTitle}>CARD: {card}</h3>
                                    <div style={styles.sectionSummary}>
                                        {cardCompletedItems} of {cardTotalItems} complete
                                        {cardRemainingItems > 0 ? ` · ${cardRemainingItems} remaining` : ' · complete'}
                                    </div>
                                </div>
                            </div>
                            <div style={styles.sectionActions}>
                                <label style={{ fontSize: '14px', marginRight: '10px' }}>Man Hours:</label>
                                <input
                                  type="number"
                                  step="0.25"
                                  placeholder="0.00"
                                  defaultValue={manHours[card]?.hours}
                                  disabled={isViewer}
                                  onBlur={(e) => handleManHoursUpdate(card, e.target.value)}
                                  style={styles.input}
                                />
                                <button 
                                    onClick={() => handleMarkGroupPassed(card)} 
                                    disabled={isViewer}
                                    style={styles.markAllButton}>Mark All Passed
                                </button>
                            </div>
                        </div>
                        {isCardExpanded && Array.isArray(groupedChecklist[card]) && (
                            <div id={`card-items-${card}`}>
                                {groupedChecklist[card].map((item) => (
                                    <ChecklistItemRow
                                        key={item.id}
                                        item={item}
                                        styles={styles}
                                        isViewer={isViewer}
                                        onFieldUpdate={handleFieldUpdate}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                    );
                })}
                
                <div style={styles.completionFooter}>
                    <div style={{ marginBottom: '12px', fontSize: '14px', color: styles.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '600' }}>{completedItems} of {totalItems} items</span>
                        <span style={{ color: styles.secondary }}>({completionPercentage}%)</span>
                    </div>
                    <button
                        style={{
                            ...styles.button,
                            backgroundColor: isComplete ? styles.success : styles.warning,
                            opacity: isViewer ? 0.6 : 1
                        }}
                        onClick={handleCompleteInspection}
                        disabled={isViewer}
                    >
                        {isComplete ? 'Mark Inspection as Complete' : `Complete All Items First (${totalItems - completedItems} remaining)`}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ChecklistPage;
